// Description: Modal that will render one table with the filtered data
// Updated: 24/07/2025
// Author: Simon Franklin

// React
import React, { useEffect, useState } from 'react';

// Material UI Components
import { Box, Chip, Tooltip, Typography } from '@mui/material';

// Components and Functions for the project
import dayjs from 'dayjs';
import themeColors from '../ThemeColors/ThemeColors';
import axios from 'axios';
import { notify } from '../../utils/utils';
import Modal from '@mui/material/Modal';
import ModeGraphicModalContent from './ModeGraphicModalContent';
import ModeGraphicModalHeader from './ModeGraphicModalHeader';
import use123zapStore from '../../context/123zapStore';

// Render The Component
const ModeGraphicModal = ({ open, onClose, data, loading, setLoading }) => {

  const setTableColumns = use123zapStore((state) => state.setTableColumns);
  const setCampaignsSummaryData = use123zapStore((state) => state.setCampaignsSummaryData);
  const setExcelTableColumns = use123zapStore((state) => state.setExcelTableColumns);
  const setExcelTableRows = use123zapStore((state) => state.setExcelTableRows);
  const setShowNewTable = use123zapStore((state) => state.setShowNewTable);
  const tableColumns = use123zapStore((state) => state.tableColumns);
  const campaignsSummaryData = use123zapStore((state) => state.campaignsSummaryData);
  const excelTableRows = use123zapStore((state) => state.excelTableRows);
  const excelTableColumns = use123zapStore((state) => state.excelTableColumns);
  const showNewTable = use123zapStore((state) => state.showNewTable);

  // General States
  const [loadingTable, setLoadingTable] = useState(true);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [progress, setProgress] = useState(0);
  const [valueSearch, setValueSearch] = useState('');
  const [loadingLabels, setLoadingLabels] = useState(true);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [filteredMockup, setFilteredMockup] = useState([]);
  const [sentMessagesLength, setSentMessagesLength] = useState(0);
  const [totalApiPages, setTotalApiPages] = useState(1);

  // New Table states
  const rowsPerPage = 100;

  useEffect(() => {
    if (data?.date) {
      const [year, month] = data.date.split('-');
      const firstDay = dayjs(`${year}-${month}-01`);
      const lastDay = dayjs(firstDay).endOf('month');

      setStartDate(firstDay);
      setEndDate(lastDay);
    }
  }, [data]);

  useEffect(() => {
    if (open && data.date !== '' && campaignsSummaryData.length === 0 && excelTableColumns.length === 0 && excelTableRows.length === 0) {
      (async () => {
        setLoading(true);
        setLoadingSearch(true);
        setLoadingLabels(true);
        setLoadingTable(true);

        const startDate = dayjs(`${data.date}-01`);
        const endDate = startDate.endOf("month");

        const url = process.env.REACT_APP_BACKEND_AWS_URL;

        setTableColumns([
          "Campanha",
          "Descrição",
          "Data Execução",
          "Entregues",
          "Lidas",
          "Não confirmadas",
          "Em espera",
          "Desconhecidas",
          "Falhas",
          "Total"
        ]);

        const body = {
          start_date: startDate.format("DD/MM/YYYY"),
          end_date: endDate.format("DD/MM/YYYY"),
          refresh: true
        };

        await fetch(`${url}/dashboard/campaigns/summary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": localStorage.getItem("jwt_token"),
            "Client_ID": localStorage.getItem("idToken")
          },
          body: JSON.stringify(body)
        })
          .then((response) => response.json())
          .then((items) => {
            const mappedData = items.data.map((item) => ({
              Campanha: item.nome_campanha,
              Descrição: item.descricao_campanha,
              "Data Execução": item.data_execucao,
              Entregues: item.status_entregue,
              Lidas: item.status_lido,
              "Não confirmadas": item.status_nao_confirmado,
              "Em espera": item.status_espera,
              Desconhecidas: item.status_desconhecido,
              Falhas: item.status_falha,
              "Total": item.mensagens_totais
            }));

            // Criar linha de totais
            const totalLine = {
              Campanha: "Total",
              Descrição: "",
              "Total": 0,
              "Data Execução": "",
              Falhas: 0,
              Lidas: 0,
              Entregues: 0,
              "Não confirmadas": 0,
              "Em espera": 0,
              Desconhecidas: 0,
            };

            // Somar valores
            mappedData.forEach((item) => {
              totalLine["Total"] += item["Total"] || 0;
              totalLine["Falhas"] += item["Falhas"] || 0;
              totalLine["Lidas"] += item["Lidas"] || 0;
              totalLine["Entregues"] += item["Entregues"] || 0;
              totalLine["Não Confirmada"] += item["Não Confirmada"] || 0;
              totalLine["Em Espera"] += item["Em Espera"] || 0;
              totalLine["Desconhecido"] += item["Desconhecido"] || 0;
            });

            const dataWithTotal = [...mappedData, totalLine];

            setTableColumns(Object.keys(dataWithTotal[0] || {}));
            setCampaignsSummaryData(dataWithTotal || {});
            setTotalItems(items.total_items);
          })
          .catch((error) => {
            console.error("Erro ao buscar os indicadores:", error);
          })
          .finally(() => {
            setLoading(false);
            setLoadingSearch(false);
            setLoadingLabels(false);
            setLoadingTable(false);
          });

      })();
    }
  }, [open, data, page])

  // Function to download campaign data in Excel format
  const downloadExcel = async () => {

    setLoadingExcel(true);

    notify('Normal', 'Seu relatório está sendo baixado', true);

    const body = {
      filter_status: null,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      name_campaign: valueSearch,
    };

    const url = process.env.REACT_APP_BACKEND_URL;

    axios.post(`${url}/painel/data/messages/general/download`, body, {
      headers: { Authorization: localStorage.getItem('jwt_token'), "clientid": localStorage.getItem('idToken') },
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      }
    })
      .then((response) => {
        const url_download = response.data.url;
        const link = document.createElement('a');
        link.href = url_download;
        link.download = url_download.split('/').pop();
        link.click();
        notify('Success', 'Seu Relatório foi baixado com sucesso!', true);
      })
      .catch((error) => {
        console.error('Erro ao buscar os indicadores:', error);
        notify('Error', 'Ocorreu algum erro no download', true);
      })
      .finally(() => {
        setLoadingExcel(false);
        setProgress(0);
      });
  };

  // Function to search campaigns
  const searchCampaigns = async () => {

    setLoadingSearch(true);
    setLoadingLabels(true);
    setLoading(true);

    const body = {
      start_date: startDate.format('DD/MM/YYYY'),
      end_date: endDate.format('DD/MM/YYYY'),
      refresh: true
    };

    const url = process.env.REACT_APP_BACKEND_AWS_URL;

    await fetch(`${url}/dashboard/campaigns/summary`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": localStorage.getItem("jwt_token"),
        "Client_ID": localStorage.getItem("idToken")
      },
      body: JSON.stringify(body)
    })
      .then(response => response.json())
      .then((items) => {
        const mappedData = items.data.map((item) => ({
          Campanha: item.nome_campanha,
          Descrição: item.descricao_campanha,
          "Data Execução": item.data_execucao,
          Entregues: item.status_entregue,
          Lidas: item.status_lido,
          "Não confirmadas": item.status_nao_confirmado,
          "Em espera": item.status_espera,
          Desconhecidas: item.status_desconhecido,
          Falhas: item.status_falha,
          "Total": item.mensagens_totais
        }));

        const totalLine = {
          Campanha: "Total",
          Descrição: "",
          "Total": 0,
          "Data Execução": "",
          Falhas: 0,
          Lidas: 0,
          Entregues: 0,
          "Não confirmadas": 0,
          "Em espera": 0,
          Desconhecidas: 0,
        };

        mappedData.forEach((item) => {
          totalLine["Total"] += item["Total"] || 0;
          totalLine["Falhas"] += item["Falhas"] || 0;
          totalLine["Lidas"] += item["Lidas"] || 0;
          totalLine["Entregues"] += item["Entregues"] || 0;
          totalLine["Não Confirmada"] += item["Não Confirmada"] || 0;
          totalLine["Em Espera"] += item["Em Espera"] || 0;
          totalLine["Desconhecido"] += item["Desconhecido"] || 0;
        });

        const dataWithTotal = [...mappedData, totalLine];

        setTableColumns(Object.keys(dataWithTotal[0] || {}));
        setCampaignsSummaryData(dataWithTotal || {});
        setTotalItems(items.total_items);
      })
      .catch(error => {
        console.error("Erro ao buscar os indicadores:", error);
      })
      .finally(() => {
        setLoading(false);
        setLoadingSearch(false);
        setLoadingLabels(false);
      });
  }

  // Function to fetch the data to render the dynamic table
  const fetchDataToNewTable = () => {
    setShowNewTable(true);
    setLoadingTable(true);

    const columnOrder = [
      'Título da campanha',
      'Data de envio',
      'Modelo da mensagem',
      'Contato destinatário',
      'Último Status',
      'Tipo de canal',
      'Data de início da campanha',
      'Data de fim da campanha',
      'ID campanha enviada',
      'ID da Mensagem',
      'Ordem da mensagem',
      'Banco',
      'Código de Barras',
      'Contrato ID',
      'CPF/CNPJ',
      'Data de Vencimento',
      'ID da Fatura',
      'Nome Completo',
      'Referência 01',
      'Referência 02',
      'Referência 03',
      'Referência 04',
      'Referência 05',
      'Valor Total',
      'WhatsApp'
    ];

    const authorization = localStorage.getItem('jwt_token');

    const body = {
      start_date: dayjs(startDate).format('DD/MM/YYYY'),
      end_date: dayjs(endDate).format('DD/MM/YYYY'),
      refresh: true,
      page: 1
    };

    const url = process.env.REACT_APP_BACKEND_AWS_URL;

    axios
      .post(`${url}/dashboard/messages/sent/lines`, body, {
        headers: {
          Authorization: authorization,
          "Client_ID": localStorage.getItem('idToken')
        }
      })
      .then((response) => {
        const formattedData = response.data.data.map((item, index) => ({
          id: (body.page - 1) * 1000 + index + 1,
          'Título da campanha': item['titulo_da_campanha'],
          'Data de envio': item['data_de_envio'],
          'Modelo da mensagem': item['modelo_da_mensagem'],
          'Contato destinatário': item['contato_destinatario'],
          'Último Status': item['ultimo_status'],
          'Tipo de canal': item['tipo_de_canal'],
          'Data de início da campanha': item['data_de_inicio_da_campanha'],
          'Data de fim da campanha': item['data_de_fim_da_campanha'],
          'ID campanha enviada': item['id_campanha_enviada'],
          'ID da Mensagem': item['id_da_mensagem'],
          'Ordem da mensagem': item['ordem_da_mensagem'],
          'Banco': (!item['variavel_banco'] || item['variavel_banco'] === "None") ? "Não informado" : item['variavel_banco'],
          'Código de Barras': (!item['variavel_codigo_barras'] || item['variavel_codigo_barras'] === "None") ? "Não informado" : item['variavel_codigo_barras'],
          'Contrato ID': (!item['variavel_contrato_id'] || item['variavel_contrato_id'] === "None") ? "Não informado" : item['variavel_contrato_id'],
          'CPF/CNPJ': (!item['variavel_cpf_cnpj'] || item['variavel_cpf_cnpj'] === "None") ? "Não informado" : item['variavel_cpf_cnpj'],
          'Data de Vencimento': (!item['variavel_data_vencimento'] || item['variavel_data_vencimento'] === "None") ? "Não informado" : item['variavel_data_vencimento'],
          'ID da Fatura': (!item['variavel_id_fatura'] || item['variavel_id_fatura'] === "None") ? "Não informado" : item['variavel_id_fatura'],
          'Nome Completo': (!item['variavel_nome_completo'] || item['variavel_nome_completo'] === "None") ? "Não informado" : item['variavel_nome_completo'],
          'Referência 01': (!item['variavel_referencia_01'] || item['variavel_referencia_01'] === "None") ? "Não informado" : item['variavel_referencia_01'],
          'Referência 02': (!item['variavel_referencia_02'] || item['variavel_referencia_02'] === "None") ? "Não informado" : item['variavel_referencia_02'],
          'Referência 03': (!item['variavel_referencia_03'] || item['variavel_referencia_03'] === "None") ? "Não informado" : item['variavel_referencia_03'],
          'Referência 04': (!item['variavel_referencia_04'] || item['variavel_referencia_04'] === "None") ? "Não informado" : item['variavel_referencia_04'],
          'Referência 05': (!item['variavel_referencia_05'] || item['variavel_referencia_05'] === "None") ? "Não informado" : item['variavel_referencia_05'],
          'Valor Total': (!item['variavel_valor_total'] || item['variavel_valor_total'] === "None") ? "Não informado" : item['variavel_valor_total'],
          'WhatsApp': (!item['variavel_whatsapp'] || item['variavel_whatsapp'] === "None") ? "Não informado" : item['variavel_whatsapp'],
          ...item
        }));

        setSentMessagesLength(response.data.total_items);
        setTotalApiPages(response.data.total_pages);

        setExcelTableRows(formattedData);

        if (formattedData.length > 0) {
          const dynamicColumns = columnOrder
            .filter((key) => key !== 'id' && key !== 'Mensagem de erro')
            .sort((a, b) => {
              const indexA = columnOrder.indexOf(a);
              const indexB = columnOrder.indexOf(b);
              if (indexA === -1 && indexB === -1) return 0;
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            })
            .map((key) => {
              // Remove acentos para exibição
              const normalizeHeader = (str) =>
                str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

              const normalizedKey = normalizeHeader(key);

              if (normalizedKey === 'Ultimo Status') {
                const statusStyles = {
                  'entrega não confirmada': {
                    color: 'warning',
                    backgroundColor: '#fff3cd',
                    tooltipMessage: 'O sistema do WhatsApp aceitou sua mensagem, mas ainda não confirmou a entrega ao destinatário.'
                  },
                  espera: {
                    color: 'info',
                    backgroundColor: '#e1f7ff',
                    tooltipMessage: 'Sua mensagem foi criada e está aguardando envio.'
                  },
                  falha: {
                    color: 'error',
                    backgroundColor: '#f8d7da',
                    tooltipMessage: 'Ocorreu uma falha no envio.'
                  },
                  entregue: {
                    color: 'primary',
                    backgroundColor: '#d1ecf1',
                    tooltipMessage: 'Sua mensagem já foi entregue ao destinatário.'
                  },
                  lida: {
                    color: 'success',
                    backgroundColor: '#E9F5E3',
                    tooltipMessage: 'O destinatário visualizou sua mensagem.'
                  },
                  desconhecido: {
                    color: 'default',
                    backgroundColor: '#f1f1f1',
                    tooltipMessage: 'Não foi possível gerar o item. Tente novamente mais tarde.'
                  },
                  default: {
                    color: 'default',
                    backgroundColor: '#f1f1f1',
                    tooltipMessage: 'Status desconhecido.'
                  }
                };

                return {
                  field: key,
                  headerName: key,
                  flex: 1,
                  minWidth: 180,
                  renderCell: (params) => {
                    const value = params.value || 'desconhecido';
                    const status = statusStyles[value.toLowerCase()] || statusStyles.default;

                    return (
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: '14px', alignContent: 'center', textAlign: 'center' }}>
                            {status.tooltipMessage}
                          </Typography>
                        }
                        arrow
                      >
                        <Chip
                          label={value}
                          color={status.color}
                          variant="outlined"
                          sx={{
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            backgroundColor: status.backgroundColor,
                            textTransform: 'capitalize',
                            minWidth: 100,
                            padding: '4px 8px',
                            cursor: 'pointer'
                          }}
                        />
                      </Tooltip>

                    );
                  }
                };
              }

              if (normalizedKey === 'Codigo de erro') {
                return {
                  field: key,
                  headerName: key,
                  flex: 1,
                  minWidth: 160,
                  renderCell: (params) => {
                    const value = params.value || 'Nenhum';

                    return (
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: '14px', alignContent: 'center', textAlign: 'center' }}>
                            {params.row['Mensagem de erro'] || 'Sem mensagem de erro'}
                          </Typography>
                        }
                        arrow
                      >
                        <Chip
                          label={value}
                          color={value !== 'Nenhum' ? 'error' : 'default'}
                          variant="outlined"
                          sx={{
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            backgroundColor: value !== 'Nenhum' ? '#f8d7da' : '#f1f1f1',
                            textTransform: 'capitalize',
                            minWidth: 100,
                            padding: '4px 8px',
                            cursor: value !== 'Nenhum' ? 'pointer' : 'default'
                          }}
                        />
                      </Tooltip>
                    );
                  }
                };
              }

              return {
                field: key,
                headerName: key,
                flex: 1,
                minWidth: 150
              };
            });

          setExcelTableColumns(dynamicColumns);

        }
      })
      .catch((error) => {
        console.error('Erro ao buscar os indicadores:', error);
      })
      .finally(() => {
        setLoadingTable(false);
      });
  };

  return (
    <Modal
      open={open}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          backgroundColor: '#F4F5FF',
          borderRadius: '0px',
          zIndex: 5000,
          position: 'relative',
          padding: { xs: '20px', sm: showNewTable && loadingTable === false ? '70px 30px 0px 30px' : '30px' },
          overflow: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: `${themeColors.gray30} ${themeColors.gray10}`,
        }}
      >
        <ModeGraphicModalHeader excelColumns={excelTableColumns} resetModal={onClose} rows={excelTableRows} setShowNewTable={setShowNewTable} showNewTable={showNewTable} loadingTable={loadingTable} />
        <ModeGraphicModalContent
          sentMessagesLength={sentMessagesLength}
          setSentMessagesLength={setSentMessagesLength}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          downloadExcel={downloadExcel}
          loadingExcel={loadingExcel}
          loadingLabels={loadingLabels}
          searchCampaigns={searchCampaigns}
          loadingSearch={loadingSearch}
          setValueSearch={setValueSearch}
          valueSearch={valueSearch}
          filteredCampaigns={filteredMockup}
          setFilteredCampaigns={setFilteredMockup}
          fetchDataToNewTable={fetchDataToNewTable}
          loadingTable={loadingTable}
          showNewTable={showNewTable}
          excelColumns={excelTableColumns}
          rows={excelTableRows}
          setRows={setExcelTableRows}
          columns={tableColumns}
          setExcelColumns={setExcelTableColumns}
          campaigns={campaignsSummaryData}
          loading={loading}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          themeColors={themeColors}
          totalApiPages={totalApiPages}
          setTotalApiPages={setTotalApiPages}
        />
      </Box>
    </Modal>
  )
}

export default ModeGraphicModal